import { create } from "zustand";
import type { Membership, MePayload, MeUser, UserProfile } from "@/lib/types";

/** Auth store — owns the materialized `/api/me` user identity.
 *
 * The fetch itself is driven by TanStack Query (`useMe` in
 * `features/auth/useMe.ts`). This store carries the cached user object
 * so non-React code (api fetch wrapper, scope resolution) can read it
 * synchronously without going through the query cache.
 *
 * The split between auth + scope stores mirrors the backend split: the
 * user identity is the same across teams; the active scope (team /
 * season / membership / capabilities) changes when the user switches.
 * Keeping them in separate stores means swapping scope doesn't
 * re-render every consumer of the user identity.
 */

type AuthState = {
  /** Latest materialized `/api/me` payload. Null after sign-out/401.
   * Public shells derive role-gated UI from this store instead of the
   * query's last-success value so `applyAnonymous()` immediately clears
   * mounted affordances across the auth boundary.
   */
  me: MePayload | null;
  user: MeUser | null;
  profile: UserProfile | null;
  memberships: Membership[];
  /** Set true while `/api/me` is in flight on first boot; flips to
   * false either way (success or 401). Lets the shell render a
   * splash without committing to a route. */
  hydrating: boolean;

  applyMePayload: (payload: MePayload) => void;
  /** Called on 401 from /api/me — distinct from `clear()` so we can
   * still tell the difference between "never hydrated" and "signed
   * out". */
  applyAnonymous: () => void;
  clear: () => void;
};

export const useAuthStore = create<AuthState>((set) => ({
  me: null,
  user: null,
  profile: null,
  memberships: [],
  hydrating: true,

  applyMePayload: (payload) =>
    set({
      me: payload,
      user: payload.user,
      profile: payload.profile,
      memberships: payload.memberships,
      hydrating: false,
    }),

  applyAnonymous: () =>
    set({
      me: null,
      user: null,
      profile: null,
      memberships: [],
      hydrating: false,
    }),

  clear: () =>
    set({
      me: null,
      user: null,
      profile: null,
      memberships: [],
      hydrating: true,
    }),
}));

/** Non-hook accessor for code outside React. */
export function currentUser(): MeUser | null {
  return useAuthStore.getState().user;
}
